import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import Integration from "@/model/interface/Integration";
import { useGetPodioAppFieldsQuery, useUpdateIntegrationSettingsMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

const CONTACT_PLATFORM_FIELDS = [
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "company", label: "Company" },
  { value: "position", label: "Position" },
  { value: "address", label: "Address" },
];

const CALL_PLATFORM_FIELDS = [
  { value: "callId", label: "Call ID" },
  { value: "direction", label: "Direction" },
  { value: "fromNumber", label: "From Number" },
  { value: "toNumber", label: "To Number" },
  { value: "duration", label: "Duration" },
  { value: "notes", label: "Call Notes" },
  { value: "recordingUrl", label: "Recording URL" },
  { value: "disposition", label: "Disposition" },
  { value: "messageBody", label: "Message Body" },
  { value: "retreevoUrl", label: "Retreevo URL" },
];

function AppFieldMapping({
  appId,
  platformFields,
  currentMapping,
  onChange,
}: {
  appId: number;
  platformFields: Array<{ value: string; label: string }>;
  currentMapping: Record<string, string>;
  onChange: (platformField: string, externalId: string | null) => void;
}) {
  const { data: fields, isLoading } = useGetPodioAppFieldsQuery(appId);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading app fields...</p>;
  }

  if (!fields || fields.length === 0) {
    return <p className="text-sm text-muted-foreground">No fields found in this Podio app</p>;
  }

  const externalIdForPlatformField = (platformField: string): string => {
    const entry = Object.entries(currentMapping).find(([, value]) => value === platformField);
    return entry ? entry[0] : "none";
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground font-medium">
        <span>Platform Field</span>
        <span>Podio Field</span>
      </div>
      {platformFields.map((platformField) => (
        <div key={platformField.value} className="grid grid-cols-2 gap-2 items-center">
          <Label className="text-sm">{platformField.label}</Label>
          <Select
            value={externalIdForPlatformField(platformField.value)}
            onValueChange={(value) => onChange(platformField.value, value === "none" ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Not mapped" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="none">Not mapped</SelectItem>
              {fields.map((field) => (
                <SelectItem key={field.fieldId} value={field.externalId}>
                  {field.label} ({field.type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

export default function PodioFieldMapping({ integration }: Props) {
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const settings = integration.settings || {};
  const contactsAppId = settings.podioContactsAppId ? Number(settings.podioContactsAppId) : undefined;
  const callsAppId = settings.podioCallsAppId ? Number(settings.podioCallsAppId) : undefined;

  const contactMapping = (settings.podioContactFieldMapping as Record<string, string>) || {};
  const callMapping = (settings.podioCallFieldMapping as Record<string, string>) || {};

  const updateMapping = async (key: string, platformField: string, externalId: string | null) => {
    const current = (settings[key] as Record<string, string>) || {};
    const updated: Record<string, string> = {};
    for (const [existingExternalId, mappedPlatform] of Object.entries(current)) {
      if (mappedPlatform !== platformField) {
        updated[existingExternalId] = mappedPlatform;
      }
    }
    if (externalId) {
      updated[externalId] = platformField;
    }
    try {
      await updateSettings({ id: integration.id, settings: { [key]: updated } }).unwrap();
    } catch {
      toast.error("Failed to update field mapping");
    }
  };

  if (!contactsAppId && !callsAppId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Configure the contacts and calls apps in the Setup tab first.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {contactsAppId && (
        <Card>
          <CardHeader>
            <CardTitle>Contacts App Fields</CardTitle>
            <CardDescription>Map Retreevo contact fields to your Podio contacts app fields</CardDescription>
          </CardHeader>
          <CardContent>
            <AppFieldMapping
              appId={contactsAppId}
              platformFields={CONTACT_PLATFORM_FIELDS}
              currentMapping={contactMapping}
              onChange={(platformField, externalId) =>
                updateMapping("podioContactFieldMapping", platformField, externalId)
              }
            />
          </CardContent>
        </Card>
      )}

      {callsAppId && (
        <Card>
          <CardHeader>
            <CardTitle>Calls App Fields</CardTitle>
            <CardDescription>Map Retreevo call and message fields to your Podio calls app fields</CardDescription>
          </CardHeader>
          <CardContent>
            <AppFieldMapping
              appId={callsAppId}
              platformFields={CALL_PLATFORM_FIELDS}
              currentMapping={callMapping}
              onChange={(platformField, externalId) =>
                updateMapping("podioCallFieldMapping", platformField, externalId)
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
